import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EventCard, { EventCardProps } from "@/components/EventCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Search, Calendar, Filter } from "lucide-react";
import { EventsService } from "@/api/events";
import { getCurrentClientData } from "@/services/authServices";
import { useToast } from "@/hooks/use-toast";
import EventSkeletonLoader from "@/components/SkeletonLoaders/EventSkeletonLoader";

// Sample events data - in a real app, this would come from an API
const allEvents: EventCardProps[] = [
  {
    id: "1",
    title: "Tech Innovation Summit 2023",
    description:
      "Join industry leaders for discussions on emerging technologies and digital transformation strategies.",
    date: "Dec 15, 2023",
    time: "9:00 AM - 5:00 PM",
    location: "San Francisco, CA",
    image:
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 380,
    maxAttendees: 400,
    price: "$299",
    category: "Technology",
    featured: true,
  },
  {
    id: "2",
    title: "Digital Marketing Masterclass",
    description:
      "Learn the latest digital marketing strategies to grow your business online.",
    date: "Nov 25, 2023",
    time: "10:00 AM - 3:00 PM",
    location: "Virtual Event",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 210,
    maxAttendees: 300,
    price: "$149",
    category: "Marketing",
  },
  {
    id: "3",
    title: "Healthcare Innovation Conference",
    description:
      "Exploring the future of healthcare technology and patient care models.",
    date: "Jan 10, 2024",
    time: "8:30 AM - 4:30 PM",
    location: "Boston, MA",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 150,
    maxAttendees: 200,
    price: "$249",
    category: "Healthcare",
  },
  {
    id: "4",
    title: "Sustainable Business Forum",
    description:
      "Strategies for implementing sustainable practices in your business operations.",
    date: "Feb 5, 2024",
    time: "9:00 AM - 4:00 PM",
    location: "Seattle, WA",
    image:
      "https://images.unsplash.com/photo-1464692805480-a69dfaafdb0d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 120,
    maxAttendees: 250,
    price: "$179",
    category: "Business",
  },
  {
    id: "5",
    title: "AI in Education Summit",
    description:
      "Exploring the applications of artificial intelligence in modern education.",
    date: "Dec 3, 2023",
    time: "10:00 AM - 3:00 PM",
    location: "Chicago, IL",
    image:
      "https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 95,
    maxAttendees: 150,
    price: "$129",
    category: "Education",
  },
  {
    id: "6",
    title: "Global Finance Conference",
    description:
      "Insights into global financial trends and investment strategies for 2024.",
    date: "Jan 20, 2024",
    time: "8:00 AM - 6:00 PM",
    location: "New York, NY",
    image:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 280,
    maxAttendees: 300,
    price: "$349",
    category: "Finance",
  },
];

const Events = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");
  const [showVirtualOnly, setShowVirtualOnly] = useState(false);
  const [dateFilter, setDateFilter] = useState("any");
  const [priceFilter, setPriceFilter] = useState("any");
  const { getEvents } = EventsService;
  const loggedInUser = getCurrentClientData();
  const [loading, setLoading] = useState(true);
  const [eventsList, setEventsList] = useState([]);
  const { toast } = useToast();

  // Filter events based on search term and filters
  const filteredEvents = allEvents.filter((event) => {
    const matchesSearch =
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = category === "all" || event.category === category;

    const matchesVirtual = showVirtualOnly
      ? event.location.includes("Virtual")
      : true;

    // Simple date filter - in a real app you'd use proper date comparisons
    const matchesDate =
      dateFilter === "any" ||
      (dateFilter === "upcoming" && new Date(event.date) >= new Date()) ||
      (dateFilter === "today" &&
        event.date === new Date().toLocaleDateString()) ||
      (dateFilter === "thisWeek" &&
        new Date(event.date) <=
          new Date(new Date().setDate(new Date().getDate() + 7)));
    const rawPrice = event.price;

    // Simple price filter
    const price = parseInt(rawPrice.replace(/[^0-9]/g, ""));
    const matchesPrice =
      priceFilter === "any" ||
      (priceFilter === "free" && price === 0) ||
      (priceFilter === "under100" && price < 100) ||
      (priceFilter === "100to250" && price >= 100 && price <= 250) ||
      (priceFilter === "above250" && price > 250);

    return (
      matchesSearch &&
      matchesCategory &&
      matchesVirtual &&
      matchesDate &&
      matchesPrice
    );
  });

  const filtered = eventsList.filter((event) => {
    const matchesSearch =
      event.event_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.event_description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = category === "all" || event.category === category;

    const matchesVirtual = showVirtualOnly
      ? event?.virtual_event === "Yes"
      : true;

    // Simple date filter - adjust based on your API's date format
    const matchesDate =
      dateFilter === "any" ||
      (dateFilter === "upcoming" && new Date(event.event_date) >= new Date()) ||
      (dateFilter === "today" &&
        new Date(event.event_date).toLocaleDateString() ===
          new Date().toLocaleDateString()) ||
      (dateFilter === "thisWeek" &&
        new Date(event.event_date) <=
          new Date(new Date().setDate(new Date().getDate() + 7)));

    // Simple price filter - adjust based on your API's price format
    const price =
      typeof event.ticket_price === "string" &&
      parseInt(event.ticket_price.replace(/[^0-9]/g, ""));

    // typeof event.ticket_price === "string"
    //   ? parseInt(event.ticket_price.replace(/[^0-9]/g, ""))
    //   : typeof event.ticket_price === "number"
    //   ? event.ticket_price
    //   : NaN;
    const matchesPrice =
      priceFilter === "any" ||
      (priceFilter === "free" && price === 0) ||
      (priceFilter === "under100" && price < 100) ||
      (priceFilter === "100to250" && price >= 100 && price <= 250) ||
      (priceFilter === "above250" && price > 250);

    return (
      matchesSearch &&
      matchesCategory &&
      matchesVirtual &&
      matchesDate &&
      matchesPrice
    );
  });

  const loadAllEventsFn = async () => {
    setLoading(true);

    try {
      const res: any = await getEvents(loggedInUser?.token);

      if (res.statusCode === 200 || res.statusCode === 201) {
        setEventsList(res.body.events);
      } else {
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error creating your event.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error creating your event.",
        variant: "destructive",
      });
      return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loggedInUser) {
      loadAllEventsFn();
    } else {
      setLoading(false);
    }
  }, []);

  const eventsToShow = loggedInUser
    ? filtered.map(
        (event) =>
          ({
            id: event.event_id,
            title: event.event_title,
            description: event.event_description,
            date: new Date(event.event_date).toLocaleDateString(),
            time: `${event.start_time} - ${event.end_time}`,
            location: event.location,
            image: event?.img_url[0],
            attendees: 0, // Update if backend provides this
            // maxAttendees: event.max_attendees,
            price: `$${event?.ticket_price ?? "0"}`,
            category: event.category,
            virtual_event: event.virtual_event === "Yes", // Adjust based on your API response
          } as unknown as EventCardProps)
      )
    : filteredEvents;

  // const eventsToShow = eventsList.length === 0 ? filteredEvents : filtered;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Page Header */}
        <div className="bg-cecure-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold">Browse Events</h1>
            <p className="mt-2">Discover and register for upcoming events</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Filter Section */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search box */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search events..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Category filter */}
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Technology">Technology</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                  <SelectItem value="Healthcare">Healthcare</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                  <SelectItem value="Education">Education</SelectItem>
                  <SelectItem value="Finance">Finance</SelectItem>
                </SelectContent>
              </Select>

              {/* Date filter */}
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Any Date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Date</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="thisWeek">This Week</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="virtualOnly"
                  checked={showVirtualOnly}
                  onCheckedChange={() => setShowVirtualOnly(!showVirtualOnly)}
                />
                <Label htmlFor="virtualOnly">Virtual Events Only</Label>
              </div>

              <Select value={priceFilter} onValueChange={setPriceFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Price Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Price</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="under100">Under $100</SelectItem>
                  <SelectItem value="100to250">$100 - $250</SelectItem>
                  <SelectItem value="above250">Above $250</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results Section */}
          {loading ? (
            <EventSkeletonLoader />
          ) : (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">
                {eventsToShow.length}{" "}
                {eventsToShow.length === 1 ? "Event" : "Events"} Found
              </h2>

              {eventsToShow.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {eventsToShow.map((event) => (
                    // <EventCard
                    //   key={event.id}
                    //   {...event}
                    //   actions={
                    //     event.isDigital
                    //       ? [
                    //           {
                    //             label: "Join Digital Event",
                    //             href: `/digital-event/${event.id}`,
                    //             primary: true,
                    //           },
                    //           {
                    //             label: "Manage",
                    //             href: `/dashboard/${event.id}`,
                    //           },
                    //         ]
                    //       : undefined
                    //   }
                    // />
                    <EventCard
                      key={event.id}
                      {...event}
                      actions={
                        event.virtual_event
                          ? [
                              {
                                label: "Join Digital Event",
                                href: `/digital-event/${event.id}`,
                                primary: true,
                              },
                              {
                                label: "Manage",
                                href: `/dashboard/${event.id}`,
                              },
                            ]
                          : [
                              {
                                label: "Manage",
                                href: `/dashboard/${event.id}`,
                                primary: true,
                              },
                            ]
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">
                    No events found
                  </h3>
                  <p className="mt-1 text-gray-500">
                    Try adjusting your search or filters to find what you're
                    looking for.
                  </p>
                  <Button
                    className="mt-6"
                    onClick={() => {
                      setSearchTerm("");
                      setCategory("all");
                      setShowVirtualOnly(false);
                      setDateFilter("any");
                      setPriceFilter("any");
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Events;
